import React, { useState } from 'react'
import {
  StyleProp,
  StyleSheet,
  TextInput as TextInputBase,
  TextInputProps as TextInputBaseProps,
  View,
  ViewStyle,
} from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { IconName, Icon, Text } from '../atoms'
import { textStyles } from './../atoms/text/style'

interface TextInputProps extends TextInputBaseProps {
  currentRef?: React.RefObject<TextInputBase>
  title?: string
  iconLeft?: IconName
  iconRight?: IconName
  iconColor?: string
  containerStyle?: StyleProp<ViewStyle>
  contentStyle?: StyleProp<ViewStyle>
  errorMessage?: string
  hasError?: boolean
  isMultiline?: boolean
  noBorder?: boolean
  noPaddingTop?: boolean
  onPressLeft?: () => void
  onPressRight?: () => void
}

export const TextInput = ({
  currentRef,
  title,
  iconLeft,
  iconRight,
  iconColor,
  containerStyle,
  contentStyle,
  errorMessage,
  hasError = false,
  isMultiline = false,
  noBorder,
  noPaddingTop = false,
  onPressLeft,
  onPressRight,
  ...props
}: TextInputProps): React.ReactElement => {
  const theme = useTheme()
  const styles = getStyle({ theme, isMultiline, noBorder })
  const textStyle = textStyles(theme).bodySmall

  const [secureVisible, setSecureVisible] = useState(true)

  return (
    <View style={containerStyle}>
      {title && (
        <Text style={styles.title} type="bodySmall" color={theme.colors.lightGrey}>
          {title}
        </Text>
      )}
      <View style={[styles.container, contentStyle, hasError && {borderColor: theme.colors.error}]}>
        {iconLeft && (
          <Icon
            name={iconLeft}
            color={iconColor || theme.colors.lightGrey}
            onPress={onPressLeft}
            size={theme.iconSize.medium}
            containerStyle={iconLeft === 'edit' && isMultiline ? styles.iconLeftEdit : styles.iconLeft}
          />
        )}
        <TextInputBase
          {...props}
          value={props.value ?? ''}
          ref={currentRef}
          clearTextOnFocus={false}
          returnKeyType="done"
          placeholderTextColor={theme.colors.lightGrey}
          autoCorrect={false}
          secureTextEntry={props.secureTextEntry && secureVisible}
          multiline={isMultiline}
          style={[textStyle, styles.input, props.style, noPaddingTop && {paddingTop: 0}]}
          autoCapitalize="none"
        />
        {iconRight && (
          <Icon
            name={iconRight === 'eye' ? (secureVisible ? 'eye' : 'eyeOff') : iconRight}
            onPress={iconRight === 'eye'? () => setSecureVisible((prevValue) => !prevValue) : undefined}
            size={theme.iconSize.medium}
            color={iconColor || theme.colors.lightGrey}
            containerStyle={iconRight === 'edit' && isMultiline ? styles.iconRightEdit : styles.iconRight}
          />
        )}
      </View>
      {errorMessage && (
        <Text style={styles.errorMessage} type="microText" color={theme.colors.error}>
          {errorMessage}
        </Text>
      )}
    </View>
  )
}

const getStyle = ({
  theme,
  isMultiline,
  noBorder,
}: {
  theme: Theme
  isMultiline: boolean
  noBorder?: boolean
}) =>
  StyleSheet.create({
    container: {
      width: '100%',
      flexDirection: 'row',
      alignItems: isMultiline ? 'flex-start' : 'center',
      paddingTop: isMultiline ? theme.spacing.xs : 0,
      paddingHorizontal: theme.spacing.md,
      borderRadius: isMultiline ? theme.border.jumbo : theme.border.jumbo * 2,
      borderWidth: noBorder ? 0 : 1,
      borderColor: theme.colors.lightGrey,
    },
    input: {
      flex: 1,
      height: isMultiline ? 154 + theme.spacing.sm : undefined,
      // Multiline inputs must start their text at the top. Without this, a
      // tall/full-height multiline field vertically centres its text on native
      // (e.g. the note editor showed the content floating mid-screen). Matches
      // the container's `alignItems: flex-start`. No-op on web (textarea).
      textAlignVertical: isMultiline ? 'top' : undefined,
      paddingLeft: theme.spacing.xs,
      paddingVertical: theme.spacing.lg,
      color: theme.colors.primary,
      textAlign: 'left',
    },
    title: {
      color: theme.colors.greyBlue,
      paddingBottom: theme.spacing.md,
    },
    errorMessage: {
      alignSelf: 'flex-start',
      paddingVertical: theme.spacing.sm,
      paddingLeft: theme.spacing.lg,
    },
    iconLeft: {
      paddingRight: theme.spacing.md,
    },
    iconRight: {
      paddingLeft: theme.spacing.md,
    },
    iconLeftEdit: {
      paddingRight: theme.spacing.md,
      paddingTop: theme.spacing.md,
    },
    iconRightEdit: {
      paddingLeft: theme.spacing.sm,
      paddingTop: theme.spacing.md,
    },
  })
